import React from "react";
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Grid,
  Group,
  Paper,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { Client, MergeClientFieldValues } from "../../../types/client";
import { formatPhoneNumber } from "../../../utils/phoneUtils";
import { getMergeDataCategories } from "./mergeDataCategories";
import styles from "./MergeConfirmStep.module.css";

interface MergeConfirmStepProps {
  records: Client[];
  winningRecord: Client;
  /** Resolved winning scalar values for the merged profile. */
  fieldValues: MergeClientFieldValues;
  /** salons.business_vertical — drives which data categories show. */
  vertical?: string | null;
  /** Gift cards feature flag — shows the Gift Cards category when enabled. */
  giftCardsEnabled?: boolean;
  /** Loyalty program active for this salon — shows the Loyalty Points category. */
  loyaltyActive?: boolean;
}

const pick = <K extends keyof MergeClientFieldValues>(
  fieldValues: MergeClientFieldValues,
  winningRecord: Client,
  key: K,
): MergeClientFieldValues[K] =>
  fieldValues[key] ??
  (winningRecord[key as keyof Client] as MergeClientFieldValues[K]);

/**
 * Step 3 of the merge wizard. Warns the merge is irreversible, previews the
 * final surviving profile, and lists the data categories that will be combined
 * (filtered to the salon's business vertical). The acknowledgement checkbox that
 * gates the action lives in the wizard footer bar, not in this step body.
 */
const MergeConfirmStep: React.FC<MergeConfirmStepProps> = ({
  records,
  winningRecord,
  fieldValues,
  vertical,
  giftCardsEnabled,
  loyaltyActive,
}) => {
  const categories = getMergeDataCategories(vertical, {
    giftCardsEnabled,
    loyaltyActive,
  });

  const firstName = pick(fieldValues, winningRecord, "first_name") ?? "";
  const lastName = pick(fieldValues, winningRecord, "last_name") ?? "";
  const email = pick(fieldValues, winningRecord, "email");
  const areaCode = pick(fieldValues, winningRecord, "area_code") ?? "";
  const phoneNumber = pick(fieldValues, winningRecord, "phone_number") ?? "";
  const addr1 = pick(fieldValues, winningRecord, "address_1");
  const addr2 = pick(fieldValues, winningRecord, "address_2");
  const city = pick(fieldValues, winningRecord, "city");
  const state = pick(fieldValues, winningRecord, "state");
  const zip = pick(fieldValues, winningRecord, "zip_code");

  const phoneDisplay =
    areaCode || phoneNumber
      ? formatPhoneNumber(`${areaCode}${phoneNumber}`)
      : null;
  const cityLine = [city, state, zip].filter(Boolean).join(", ");
  const initials =
    `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.toUpperCase() || "?";

  return (
    <Stack gap="lg">
      <Alert
        color="yellow"
        icon={<IconAlertTriangle size={20} />}
        title="Client merges are final and cannot be undone."
      >
        You are about to merge {records.length} client records into a single
        profile. All historical data, appointments, and notes will be combined
        under the winning profile shown below. The other records will be
        permanently deleted.
      </Alert>

      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <Text className={styles.sectionLabel}>FINAL PROFILE</Text>
          <Paper withBorder radius="md" p="lg" mt="xs">
            <Stack gap="sm" align="flex-start">
              <Avatar radius="xl" size={64} color="var(--brand-teal)">
                {initials}
              </Avatar>
              <Title order={4}>
                {firstName} {lastName}
              </Title>
              {phoneDisplay && (
                <Box>
                  <Text size="xs" c="dimmed">
                    Phone
                  </Text>
                  <Text>{phoneDisplay}</Text>
                </Box>
              )}
              {email && (
                <Box>
                  <Text size="xs" c="dimmed">
                    Email
                  </Text>
                  <Text>{email}</Text>
                </Box>
              )}
              {(addr1 || cityLine) && (
                <Box>
                  <Text size="xs" c="dimmed">
                    Address
                  </Text>
                  {addr1 && <Text>{addr1}</Text>}
                  {addr2 && <Text>{addr2}</Text>}
                  {cityLine && <Text>{cityLine}</Text>}
                </Box>
              )}
            </Stack>
          </Paper>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 8 }}>
          <Text className={styles.sectionLabel}>DATA TO BE COMBINED</Text>
          <Paper withBorder radius="md" mt="xs">
            <Box className={styles.categoryIntro}>
              <Text size="sm" c="dimmed">
                The following information from all {records.length} records will
                be merged into the final profile:
              </Text>
            </Box>
            {categories.map((cat) => {
              const Icon = cat.icon;
              return (
                <Group
                  key={cat.key}
                  justify="space-between"
                  className={styles.categoryRow}
                >
                  <Group gap="md">
                    <Box className={styles.categoryIcon}>
                      <Icon size={20} />
                    </Box>
                    <Text fw={500}>{cat.label}</Text>
                  </Group>
                  {cat.badge && (
                    <Badge
                      color="var(--brand-teal)"
                      variant="light"
                      radius="sm"
                    >
                      {cat.badge}
                    </Badge>
                  )}
                </Group>
              );
            })}
          </Paper>
        </Grid.Col>
      </Grid>
    </Stack>
  );
};

export default MergeConfirmStep;
